import { useEffect } from 'react';
import Image from 'next/image';
import { X } from 'lucide-react';
import cn from 'classnames';
import { useCookies } from 'react-cookie';
import { REEBELO_DOMAINS } from '@lambda/reebelo';
import { getAllCountries } from './ExtendedNavbar/TopNavbar/StoreSelector/constants';
import i18n from '@/settings/i18n';

const t = i18n.init('shared');

/** Set when the visitor dismisses — ask again, but not for a month. */
const BANNER_DISMISSED_COOKIE = 'reebelo-country-banner-dismissed';

const THIRTY_DAYS_IN_SECONDS = 30 * 24 * 60 * 60;

/** Space the banner takes up: its 56px height plus a 16px gap above it. */
const BANNER_OFFSET = '72px';

const getStoreLink = (store: string) => {
  const protocol = process.env.NEXT_PUBLIC_STAGE === 'dev' ? 'http' : 'https';
  const domain = REEBELO_DOMAINS[store as keyof typeof REEBELO_DOMAINS];

  return `${protocol}://${domain}`;
};

const SwitchCountryBanner = ({
  countryCode,
  className,
}: {
  countryCode?: string;
  className?: string;
}) => {
  const [cookies, setCookie] = useCookies([BANNER_DISMISSED_COOKIE]);

  const country = getAllCountries().find(({ code }) => code === countryCode);

  const hasDismissed = Boolean(cookies[BANNER_DISMISSED_COOKIE]);

  const isVisible = Boolean(country) && !hasDismissed;

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    // Lets other fixed bottom elements (e.g. the cookie banner) sit above this one
    const root = document.documentElement;

    root.style.setProperty('--country-banner-offset', BANNER_OFFSET);

    return () => {
      root.style.removeProperty('--country-banner-offset');
    };
  }, [isVisible]);

  if (!country || hasDismissed) {
    return null;
  }

  const onDismiss = () => {
    setCookie(BANNER_DISMISSED_COOKIE, '1', {
      path: '/',
      maxAge: THIRTY_DAYS_IN_SECONDS,
    });
  };

  return (
    <div
      className={cn(
        // Sits above pages' own sticky bottom bars (e.g. the PDP BottomBar) via the shared offset var
        // The offset only applies below lg, where the PDP BottomBar is visible
        // Fixed px height so BANNER_OFFSET stays in step with it
        'fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+var(--cookie-banner-offset,0px))] z-[65] h-[56px] w-full bg-gray-700 text-white lg:bottom-0',
        className,
      )}
    >
      <div className="reebelo-container flex h-full items-center justify-between gap-3 md:justify-center">
        <p className="text-xs font-medium xxs:text-sm md:text-center">
          {t`Shopping from ${{ country: country.country }}?`}
        </p>

        <div className="flex shrink-0 items-center gap-1">
          <a
            href={getStoreLink(country.store)}
            className="flex h-8 items-center gap-2 rounded-lg bg-white px-3 text-xxs font-medium text-gray-700 duration-200 ease-in-out hover:opacity-90 xxs:text-xs"
          >
            <span className="flex h-5 w-5 shrink-0 items-center">
              <Image
                src={country.flag}
                alt={country.country}
                width={20}
                height={20}
                className="h-full w-full object-contain"
              />
            </span>
            {t`Switch to ${{ code: country.code }}`}
          </a>

          <button
            type="button"
            aria-label={t`Close`}
            onClick={onDismiss}
            className="flex h-8 w-8 items-center justify-center duration-150 ease-in-out hover:opacity-70"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SwitchCountryBanner;
